import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Package, Tag, Grid, X, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import debounce from 'lodash.debounce';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SearchBar = ({ className = '', onSearch }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Debounced fetch suggestions
  const fetchSuggestions = useCallback(
    debounce(async (query) => {
      if (query.length < 2) {
        setSuggestions([]);
        return;
      }

      setIsLoading(true);
      try {
        const response = await axios.get(`${API}/products/suggestions`, {
          params: { q: query, limit: 8 }
        });
        setSuggestions(response.data.suggestions || []);
        setIsOpen(true);
      } catch (error) {
        console.error('Search suggestions error:', error);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }, 300),
    []
  );

  useEffect(() => {
    if (searchTerm) {
      fetchSuggestions(searchTerm);
    } else {
      setSuggestions([]);
      setIsOpen(false);
    }
  }, [searchTerm, fetchSuggestions]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e) => {
    e?.preventDefault();
    if (searchTerm.trim()) {
      setIsOpen(false);
      navigate(`/products?search=${encodeURIComponent(searchTerm)}`);
      onSearch?.();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion.value);
    setIsOpen(false);
    
    if (suggestion.type === 'brand') {
      navigate(`/products?brand=${encodeURIComponent(suggestion.value)}`);
    } else if (suggestion.type === 'category') {
      navigate(`/products?category=${encodeURIComponent(suggestion.value.toLowerCase())}`);
    } else {
      navigate(`/products?search=${encodeURIComponent(suggestion.value)}`);
    }
    onSearch?.();
  };

  const handleKeyDown = (e) => {
    if (!isOpen || suggestions.length === 0) {
      if (e.key === 'Enter') {
        handleSearch(e);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          handleSuggestionClick(suggestions[highlightedIndex]);
        } else {
          handleSearch(e);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'brand':
        return <Tag className="w-4 h-4 text-purple-500" />;
      case 'category':
        return <Grid className="w-4 h-4 text-blue-500" />;
      default:
        return <Package className="w-4 h-4 text-slate-400" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'brand':
        return 'Brand';
      case 'category':
        return 'Category';
      default:
        return 'Product';
    }
  };

  return (
    <div className={`relative ${className}`}>
      <form onSubmit={handleSearch} className="relative">
        <Input
          ref={inputRef}
          type="text"
          placeholder="Search products, brands, categories..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setHighlightedIndex(-1);
          }}
          onFocus={() => suggestions.length > 0 && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          className="w-full h-10 pl-4 pr-20 rounded-lg border-2 border-slate-200 focus:border-[#0047AB] bg-slate-50"
          data-testid="header-search"
        />
        
        {/* Clear button */}
        {searchTerm && (
          <button
            type="button"
            onClick={() => {
              setSearchTerm('');
              setSuggestions([]);
              setIsOpen(false);
              inputRef.current?.focus();
            }}
            className="absolute right-12 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        
        {/* Search button */}
        <button
          type="submit"
          className="absolute right-1 top-1/2 -translate-y-1/2 bg-[#0047AB] text-white p-2 rounded-md hover:bg-[#003380] transition-colors"
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Search className="w-4 h-4" />
          )}
        </button>
      </form>

      {/* Suggestions Dropdown */}
      {isOpen && suggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border border-slate-200 z-50 overflow-hidden"
        >
          <div className="py-2">
            {suggestions.map((suggestion, index) => (
              <button
                key={`${suggestion.type}-${suggestion.value}`}
                onClick={() => handleSuggestionClick(suggestion)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                  index === highlightedIndex
                    ? 'bg-blue-50 text-[#0047AB]'
                    : 'hover:bg-slate-50'
                }`}
              >
                {getIcon(suggestion.type)}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{suggestion.value}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  suggestion.type === 'brand' 
                    ? 'bg-purple-100 text-purple-700'
                    : suggestion.type === 'category'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-slate-100 text-slate-600'
                }`}>
                  {getTypeLabel(suggestion.type)}
                </span>
              </button>
            ))}
          </div>
          
          {/* Search all results */}
          <button
            onClick={handleSearch}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-50 text-[#0047AB] font-medium text-sm border-t border-slate-100 hover:bg-slate-100 transition-colors"
          >
            <Search className="w-4 h-4" />
            Search for "{searchTerm}"
          </button>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
